import { X, Lightbulb, Sparkles } from "lucide-react";

/**
 * Explainability modal for any AI-generated value.
 * Usage:  <WhyModal open={...} onClose={...} title="Blank Weight" data={blankObj} />
 * `data` can be:
 *   - blank object    (has `formula` + `explanation`)
 *   - op object       (has `formula` + `explanation` + machine info)
 *   - route step      (has `reason` + `source`)
 *   - identity/geometry AI field (has `value` + `confidence`)
 */
export default function WhyModal({ open, onClose, title, data, drawingRef }) {
    if (!open) return null;

    const { formula, explanation, reason, source, confidence, value } = data || {};

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
             onClick={onClose}>
            <div
                className="bg-white rounded-lg shadow-2xl max-w-2xl w-[92vw] max-h-[80vh] overflow-auto border border-slate-200"
                onClick={(e) => e.stopPropagation()}
                data-testid="why-modal"
            >
                <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between bg-gradient-to-r from-amber-50 to-white">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[hsl(var(--copper))]/10 flex items-center justify-center">
                            <Lightbulb className="w-5 h-5 text-[hsl(var(--copper))]" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Why? · Engineering Explanation</div>
                            <div className="text-[15px] font-semibold text-slate-800">{title}</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
                        <X className="w-4 h-4 text-slate-500" />
                    </button>
                </div>

                <div className="p-5 space-y-4 text-[13px] text-slate-700">
                    {value !== undefined && (
                        <Row label="Extracted value">
                            <span className="font-mono text-slate-900">{String(value)}</span>
                        </Row>
                    )}
                    {confidence !== undefined && confidence !== null && (
                        <Row label="Confidence">
                            <ConfidencePill v={confidence} />
                        </Row>
                    )}
                    {formula && (
                        <Row label="Formula used">
                            <code className="text-[12px] bg-slate-100 px-2 py-1 rounded font-mono">{formula}</code>
                        </Row>
                    )}
                    {explanation && (
                        <Row label="Calculation steps">
                            <p className="leading-relaxed">{explanation}</p>
                        </Row>
                    )}
                    {reason && (
                        <Row label="Engineering reason">
                            <p className="leading-relaxed">{reason}</p>
                        </Row>
                    )}
                    {source && (
                        <Row label="Source">
                            <p className="text-slate-500 text-[12px]">{source}</p>
                        </Row>
                    )}
                    {drawingRef && (
                        <Row label="Drawing reference">
                            <p className="text-slate-500 text-[12px]">{drawingRef}</p>
                        </Row>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 text-[11px] text-slate-500 flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-[hsl(var(--copper))]" />
                    Formulas are deterministic. Only the drawing extraction is intelligently derived; override any value if needed.
                </div>
            </div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="grid grid-cols-[140px_1fr] gap-3">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold pt-0.5">{label}</div>
            <div>{children}</div>
        </div>
    );
}

function ConfidencePill({ v }) {
    const pct = Math.round(v * 100);
    const tone = pct >= 85 ? "emerald" : pct >= 65 ? "amber" : "red";
    const map = {
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        amber:   "bg-amber-50 text-amber-700 border-amber-200",
        red:     "bg-red-50 text-red-700 border-red-200",
    };
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-mono ${map[tone]}`}>
            {pct}%
        </span>
    );
}
